import type { MediaItem, MediaKind } from '@/lib/media';

/**
 * Builds the markup that embeds a media item in an article or
 * publication section.
 *
 * Only images have a native embed syntax in Djot and AsciiDoc. For
 * video, audio, and PDFs the markup is a plain **link** rather than an
 * embed — deliberately, because:
 *
 *  - Wikistar's own renderers (`DjotContent`/`AsciiDocContent`) only
 *    render `<img>` for image nodes; there's no `<video>`/`<audio>`
 *    node type, so an "embed" would render as nothing.
 *  - NIP-54 content is meant to be portable to other Nostr wiki
 *    clients. A link degrades gracefully everywhere; a bespoke embed
 *    syntax would render as literal punctuation in clients that don't
 *    know it.
 *
 * A link that works in every client beats an embed that works in one.
 */

/** The placeholder URL scheme used for media that hasn't been uploaded yet. */
export const PENDING_MEDIA_SCHEME = 'wikistar-media:';

/**
 * A reference to a not-yet-uploaded local file, written into the draft
 * in place of a real URL.
 *
 * The sha256 is the payload, so at publish time the pending reference
 * can be matched back to its library item, uploaded, and rewritten to
 * the real Blossom URL (see `resolvePendingMedia`). Using a custom
 * scheme rather than a plausible-looking http URL is intentional: it
 * can never be mistaken for a working link, and `sanitizeUrl()` will
 * refuse to render it, so a pending image visibly fails in preview
 * instead of silently looking fine.
 */
export function pendingMediaUrl(sha256: string): string {
  return `${PENDING_MEDIA_SCHEME}${sha256}`;
}

export function isPendingMediaUrl(url: string): boolean {
  return url.startsWith(PENDING_MEDIA_SCHEME);
}

/** Extracts the sha256 from a pending-media placeholder URL. */
export function pendingMediaHash(url: string): string | undefined {
  if (!isPendingMediaUrl(url)) return undefined;
  const hash = url.slice(PENDING_MEDIA_SCHEME.length).trim();
  return /^[0-9a-f]{64}$/i.test(hash) ? hash.toLowerCase() : undefined;
}

/** Finds every pending-media hash referenced anywhere in a content string. */
export function findPendingMediaHashes(content: string): string[] {
  const matches = content.matchAll(new RegExp(`${PENDING_MEDIA_SCHEME}([0-9a-f]{64})`, 'gi'));
  return [...new Set([...matches].map((m) => m[1].toLowerCase()))];
}

/**
 * Replaces every pending-media placeholder with its real URL. Called at
 * publish time once uploads have completed, so the signed `content`
 * never contains a placeholder that readers couldn't resolve.
 */
export function resolvePendingMedia(content: string, urlByHash: Map<string, string>): string {
  return content.replace(new RegExp(`${PENDING_MEDIA_SCHEME}([0-9a-f]{64})`, 'gi'), (match, hash: string) => {
    return urlByHash.get(hash.toLowerCase()) ?? match;
  });
}

export type MarkupFormat = 'djot' | 'asciidoc';

/** Escapes characters that would break out of a Djot/AsciiDoc link label. */
function escapeLabel(text: string, format: MarkupFormat): string {
  return format === 'djot' ? text.replace(/([[\]\\])/g, '\\$1') : text.replace(/([[\]])/g, '\\$1');
}

/**
 * The markup for embedding/linking a media item.
 *
 * `url` is passed separately from the item because it may be either the
 * item's real Blossom URL or a pending placeholder, depending on whether
 * it's been uploaded yet.
 */
export function mediaMarkup(
  kind: MediaKind,
  url: string,
  label: string,
  format: MarkupFormat,
): string {
  const safeLabel = escapeLabel(label, format);

  if (kind === 'image') {
    // Djot: ![alt](url) — AsciiDoc: image::url[alt] (block form, so it
    // sits on its own line the way an article image should).
    return format === 'djot' ? `![${safeLabel}](${url})` : `image::${url}[${safeLabel}]`;
  }

  return format === 'djot' ? `[${safeLabel}](${url})` : `link:${url}[${safeLabel}]`;
}

/**
 * A sensible default label: the user's alt text if they've written one,
 * otherwise the filename. Never empty — an empty alt on a link would
 * render as an unclickable gap.
 */
export function defaultMediaLabel(item: MediaItem): string {
  return item.alt?.trim() || item.name || 'media';
}
